/**
 * Pure decision logic for the AuthLayout loading gate.
 *
 * Background (production blank-screen bug): inside the mobile WebView, the gate
 * used to keep the full-page spinner up while `!brandsQuery.data && isLoggedIn`,
 * and it had no terminal escape — the routed page never mounted (so it never
 * emitted PAGE_READY) and the user saw a permanent blank/white screen. Only
 * killing + relaunching the app fixed it (a fresh token was already persisted).
 *
 * There are TWO distinct token-failure sub-cases, and the gate hangs on a
 * DIFFERENT arm in each — the original diagnosis ("checked data, never isError")
 * only described the second:
 *  1. LOCALLY expired token (the dominant incident trigger): the query is DISABLED, so
 *     it never fires, never 401s, `isError` stays false and `data` stays undefined.
 *     Be precise about WHY, because there are two candidate mechanisms and only one is
 *     operative here: queries.utils.tsx defaults auth'd queries to
 *     `enabled: !AuthUtils.isExpired()`, but it spreads the caller's options AFTER that
 *     key, so AuthLayout's own `enabled: isLoggedIn` overrides it. The effective gate is
 *     `isLoggedIn` — which Auth.store.tsx initialises to `!AuthUtils.isExpired()`, hence
 *     the same outcome by a different route. (Worth knowing: any caller passing `enabled`
 *     silently defeats the expiry default.)
 *     The old gate hung here on the OTHER arm, `!isWebViewReady`, because native never
 *     answered the handshake — isLoggedIn is false from the first render, so the
 *     "already logged in → ready" effect never runs.
 *  2. SERVER-rejected token (revoked/rotated/deleted user, token not yet locally
 *     expired): the query IS enabled, fires, and 401s → `isError` true, `data`
 *     undefined forever. This is the arm the old `!data`-only check missed.
 *
 * These pure functions encode the fix so it can be unit-tested:
 *  - the gate never hangs once brands has errored (render + recover instead),
 *  - it never hangs on the handshake arm (bounded recovery drives !isWebViewReady
 *    to a terminal render), and
 *  - it never spins forever once bounded auth-recovery attempts are exhausted.
 *
 * OFFLINE is a TERMINAL state here, not an exception to the rule. Recovery deliberately
 * won't reload with no connection (that yields a real network-error page, which in a
 * WebView is as unrecoverable as a blank one), so it burns no attempts and
 * `recoveryExhausted` never arrives — which means offline cannot be allowed to reach the
 * spinner at all, or it hangs there with no bound and nothing to release it. An earlier
 * version accepted exactly that as a "silent-and-correct" indefinite spinner, on two false
 * premises: that native's 20s PAGE_READY fallback would still lift the overlay (it is armed
 * in `handleLoadEnd`, which early-returns on a same-base recovery reload — mobile-app
 * WebviewWrapper.tsx — so it frequently does not re-arm), and that `navigator.onLine`
 * eventually corrects itself (an Android WebView can report `false` spuriously at cold start
 * and never fire the `online` event that would clear it). Both wrong in the same direction,
 * and their product is the permanent blank screen this file exists to remove.
 *
 * So offline renders the explicit "No connection" screen immediately: honest copy, a live
 * Retry, PAGE_READY posted, and it steps aside on the `online` event when a genuine transition
 * occurs. It does NOT rely on that event arriving, because in the spurious-`false` case above
 * none ever will — AuthLayout re-reads `navigator.onLine` on the Retry tap, and the gate's
 * brands query runs with `networkMode: 'always'` so a lying signal cannot leave it paused with
 * no request, no error and no data. Three independent ways out; the old version had none. See
 * shouldShowWebviewAuthError and shouldAttemptWebviewAuthRecovery.
 */

import { MAX_WEBVIEW_AUTH_RECOVERIES } from 'src/services/Auth/webview-recovery-pacing.logic';

export interface AuthGateState {
  /** Running inside the React Native WebView. */
  isWebView: boolean;
  /** WebView auth handshake completed (token received or already logged in). */
  isWebViewReady: boolean;
  /** Auth store reports a (possibly stale) logged-in session. */
  isLoggedIn: boolean;
  /**
   * brandsQuery has resolved with a payload. Named for what it is: this is
   * `!!brandsQuery.data`, which is truthy for an EMPTY brand list too. It means
   * "the query answered", not "the user owns brands" — ProtectedRoute's
   * `!!data?.items?.length` is the check for the latter. The gate only needs the
   * former: an empty list is a perfectly renderable app state.
   */
  brandsLoaded: boolean;
  /** brandsQuery settled into an error (e.g. a 401 on a stale token). */
  brandsErrored: boolean;
  /**
   * That error was an AUTH failure (401/403) rather than a server or network fault.
   *
   * Distinguishing them matters twice over: re-authenticating cannot fix a 500, so waiting
   * out the recovery budget on one only delays the terminal screen past native's 20s
   * fallback; and telling a user with a perfectly valid token that their "session expired"
   * while the backend is down is simply false.
   */
  brandsErrorIsAuth: boolean;
  /**
   * brandsQuery currently has a request in flight. Distinguishes "slow but
   * progressing" from "stalled", so recovery doesn't reload the page out from
   * under a request that was merely taking its time.
   */
  brandsFetching: boolean;
  /**
   * The browser reports no network connection (`navigator.onLine === false`).
   *
   * Needed as its own input because `brandsFetching` cannot cover the handshake arm:
   * `!isWebViewReady` implies logged-out (use-webview-auth sets ready on every render
   * where isWebView && isLoggedIn), logged-out disables the brands query
   * (AuthLayout passes `enabled: isLoggedIn`, which wins — see the header), and a
   * disabled query reports `fetchStatus: 'idle'` — never 'paused'. So brandsFetching is
   * always false exactly where that arm decides anything, and the offline protection it
   * appeared to give was inert.
   */
  isOffline: boolean;
  /**
   * A manual retry is pending OR has failed — i.e. the terminal screen owns the moment.
   *
   * Covers BOTH, not just "pending", and that is the point. Retry refetches brands, which
   * flips brandsFetching true, so `!brandsFetching` alone would unmount the screen on the
   * very tap that started it. And when the retry times out while the request is still in
   * flight — precisely the hung-request case the timeout exists for — clearing this on
   * timeout would unmount the screen again, this time swallowing the "close and reopen the
   * app" message it had just decided to show and replacing it with an indefinite spinner.
   * The screen stays until it is genuinely no longer needed.
   */
  isRetrying: boolean;
  /** Bounded WebView auth-recovery attempts have been used up. */
  recoveryExhausted: boolean;
  /** Non-WebView global loading flags. */
  isGlobalLoading: boolean;
  isInitialLoading: boolean;
}

/**
 * Native round-trip pacing — the recovery budget, the request cadence, the retry timeout.
 *
 * Re-exported, not defined here. Every one of these values is a property of the NATIVE
 * handshake rather than of this layout, and `WebView.auth` needs the budget too — so defining
 * them here made `Auth.store → WebView.auth → webview-auth-reply.logic → components/ui/layouts/…`,
 * i.e. authentication transitively depending on the component layer. They live in
 * `services/Auth/webview-recovery-pacing.logic` now; re-exported so AuthLayout still has a
 * single import site for everything the gate needs.
 */
export {
  MAX_WEBVIEW_AUTH_RECOVERIES,
  RECOVERY_REPLY_WINDOW_MS,
  FIRST_RECOVERY_DELAY_MS,
  RETRY_FEEDBACK_MS,
  getRecoveryDelayMs,
} from 'src/services/Auth/webview-recovery-pacing.logic';

// The key for the attempt counter lives in StorageService's registry
// (STORAGE_KEYS.SESSION.WV_AUTH_RECOVERY_ATTEMPTS) because that service has to know to
// PRESERVE it across logout cleanup — see SESSION_STORAGE_PRESERVED_KEYS there.

/**
 * Whether AuthLayout should render the full-page spinner.
 *
 * WebView rule: wait for the auth handshake and brands to load, but NEVER hang.
 *
 * A brands failure with NO cached data does not immediately render the page. The reason is
 * NOT "ProtectedRoute would misredirect" any more — that hole is now closed at the router
 * (protected-route.tsx gates the create-brand redirect on `!brandsQuery.isError`, so an
 * errored query no longer reads as "this customer owns no brands"). Saying so explicitly
 * because the old rationale was load-bearing here and is now stale: relying on this gate to
 * hide a router bug meant every other route into that branch stayed broken.
 *
 * It holds the spinner for a smaller, WebView-only reason: while bounded recovery still has
 * attempts left, re-auth is the thing most likely to fix a data-less auth failure, and a
 * WebView has no address bar to retry from if we render a half-empty app instead. Once
 * recovery is exhausted AND nothing is in flight, the spinner stops and AuthLayout renders
 * the explicit terminal screen (see shouldShowWebviewAuthError) — never a permanent blank.
 */
export const shouldShowAuthLoading = (s: AuthGateState): boolean => {
  if (s.isWebView) {
    // A rendered terminal screen is never "still loading". Without this the two derived
    // booleans contradict each other while the retry screen is up — AuthLayout early-returns
    // the screen, but shouldSignalWebviewReady is derived from !shouldShowAuthLoading, so
    // READY was withheld for the whole retry window, breaking the invariant that every
    // terminal render path signals.
    if (shouldShowWebviewAuthError(s)) return false;
    // "Gave up" only counts once nothing is in flight. Otherwise the counter — which
    // survives the reload a successful recovery causes — makes the FIRST render after
    // a successful re-auth show the terminal error screen, while the fresh token's
    // brands request is still loading perfectly fine.
    if (s.recoveryExhausted && !s.brandsFetching) return false;
    // Offline must never reach the spinner arms below. Recovery declines to reload with no
    // connection, so it burns no attempts, so recoveryExhausted never arrives and nothing
    // else would ever release the spinner. shouldShowWebviewAuthError already caught the
    // logged-IN case above; this is the logged-OUT one, where the honest terminal render is
    // the login form (reachable, and its own failure is a legible toast rather than a blank
    // screen) rather than a spinner with no bound.
    if (s.isOffline) return false;
    if (!s.isWebViewReady) return true; // still waiting on the token handshake
    // Only a failure with NO usable data holds the spinner. React Query keeps `data`
    // when a REFETCH fails, so brandsLoaded && brandsErrored is reachable mid-session
    // on any flaky-network refetch; treating that as fatal threw a full-page spinner
    // over a working app and then hard-reloaded it, losing unsaved work.
    if (s.brandsErrored && !s.brandsLoaded) return true;
    return s.isLoggedIn && !s.brandsLoaded; // logged in, brands still loading
  }

  // Regular web: keep the original flags, but also don't hang on a brands error.
  //
  // An earlier version of this line justified itself as "accepting on web the wrong-redirect
  // risk we refuse in the WebView, because web users have an address bar". That trade was not
  // ours to make: it silently changed desktop behaviour so that one transient brands 401/429
  // dropped a subscribed customer into first-brand onboarding. The redirect is fixed at its
  // source instead (protected-route.tsx: `!brandsQuery.isError`), so rendering the page here
  // is now safe rather than merely survivable, and there is no asymmetry left to defend.
  if (s.isGlobalLoading || s.isInitialLoading) return true;
  return s.isLoggedIn && !s.brandsLoaded && !s.brandsErrored;
};

/**
 * Whether AuthLayout should render the explicit "session expired — tap to retry"
 * screen. True only in the WebView once bounded recovery is exhausted AND we still
 * have no brands (so the app can't render). If brands loaded late (recovered after
 * exhaustion) brandsLoaded is true → we fall through to the page instead. This is the
 * fix for "escape hatch swaps a blank screen for a false paywall": we render an
 * actionable retry state rather than letting the router redirect to the paywall.
 *
 * Requires isLoggedIn. AuthLayout is the router root and wraps the PUBLIC routes too
 * (app-routes.tsx — builtRoutes are its children), so without this a genuinely
 * logged-out WebView user — who never gets isWebViewReady, since use-webview-auth
 * only sets it when isLoggedIn — would exhaust recovery and have the login form
 * replaced by "Session expired", with a Retry that cannot help. "Session expired"
 * is only honest when there was a session: logged out means fall through to the
 * routed page, i.e. the login form.
 */
export const shouldShowWebviewAuthError = (s: AuthGateState): boolean => {
  if (!s.isWebView || !s.isLoggedIn || s.brandsLoaded) return false;

  // OFFLINE is terminal on its own, and deliberately skips the in-flight guard below.
  //
  // It has to be terminal because it is the one stall arm with NO bound: recovery refuses to
  // reload with no connection, so no attempt is ever spent and recoveryExhausted never
  // becomes true. Anything short of terminal here is an unbounded spinner.
  //
  // And it may skip the in-flight guard because that guard exists to stop us CLAIMING FAILURE
  // over a live request — while the "No connection" copy claims none. It says the app will
  // reconnect by itself, which is exactly true of the `paused` query underneath it. (A paused
  // query has not issued a request at all; `brandsFetching` folds 'paused' in with 'fetching'
  // on purpose elsewhere, so without this exception a logged-in offline user would be held on
  // the spinner by their own paused query.)
  if (s.isOffline) return true;

  // Recovery only has to be exhausted when re-authenticating could plausibly help. On a
  // SERVER error it cannot, so waiting out the budget just pushes the terminal render past
  // native's 20s fallback — the exact failure this file exists to remove.
  const gaveUp = s.recoveryExhausted || (s.brandsErrored && !s.brandsErrorIsAuth);

  // Never claim failure while a request is still in flight — UNLESS that request is this
  // screen's own retry. The counter survives the reload a SUCCESSFUL recovery triggers,
  // so without the first half a reloaded page would greet a freshly re-authed user with
  // "Session expired" while their brands request loads normally. But Retry itself
  // refetches brands, so without the isRetrying exception the screen would unmount on
  // the very tap that started it, replacing its own pending state with a bare spinner.
  return gaveUp && (s.isRetrying || !s.brandsFetching);
};

/**
 * Which terminal message the WebView error screen should show.
 *
 * The screen used to say "Session expired — we couldn't refresh your session" in every
 * case, which is false in two of the three:
 *  - `offline`: the user has no connection. Their paused query will resume by itself on
 *    reconnect, so telling them to close and reopen the app is both wrong and unhelpful.
 *  - `server`: brands failed with a non-auth status. The token is fine and the backend is
 *    not; re-auth cannot help and "session expired" misdirects them (and us, in reports).
 *  - `session`: an actual auth failure — the only case the original copy fitted.
 */
export type WebviewAuthErrorKind = 'offline' | 'server' | 'session';

export const getWebviewAuthErrorKind = (s: AuthGateState): WebviewAuthErrorKind => {
  if (s.isOffline) return 'offline';
  if (s.brandsErrored && !s.brandsErrorIsAuth) return 'server';
  return 'session';
};

export interface RetryButtonState {
  /** Disable the button — a retry is genuinely still in flight. */
  disabled: boolean;
  /**
   * Show the pending label instead of "Retry", AND arm the failure timer.
   *
   * One boolean for both on purpose: "the button says Reconnecting…" and "we are still
   * waiting for native" are the same fact, so the screen's effect depends on this rather than
   * on a hand-listed `[isRetrying, kind]`. That hand-listed version omitted `hasFailed`, so a
   * second tap cleared the failure without re-arming the timer and the button latched
   * disabled forever — the same dead-end this whole state exists to prevent, one tap later.
   */
  pending: boolean;
}

/**
 * What the terminal screen's Retry button should do.
 *
 * `isRetrying` alone is the wrong thing to read. It is owned by AuthLayout and — by design —
 * is NOT cleared when a retry times out: clearing it there would fail the
 * `(isRetrying || !brandsFetching)` conjunct while the retry's own refetch is still in
 * flight, unmounting the screen and swallowing the "close and reopen" message it had just
 * decided to show. So on the failure path `isRetrying` stays true for as long as the screen
 * is the rendered screen, which is forever when native isn't answering.
 *
 * A button wired straight to `isRetrying` is therefore dead after the first tap: it sits
 * disabled reading "Reconnecting…" underneath copy that asks the user to try again. Retry is
 * the only affordance on this screen, so that has to be impossible.
 *
 * Three inputs, and `kind` is the third for a reason: while OFFLINE there is no failure timer
 * (the request is paused, not failing, and telling someone with no signal to close the app is
 * wrong), so `hasFailed` never becomes true and a pending state would latch forever. Nothing
 * is in flight to wait for either — so offline is never pending, and the button stays
 * tappable next to copy that says it will reconnect by itself. Resolving it here rather than
 * by forcing `hasFailed` matters: `hasFailed` also selects the "close and reopen the app"
 * body copy, which would contradict the offline message.
 *
 * Pure and here rather than inline in the component so the node-env runner can gate it — an
 * inline `disabled={isRetrying}` is exactly the kind of one-token decision that ships
 * untested and strands the last affordance the user has.
 */
export const getRetryButtonState = (
  isRetrying: boolean,
  hasFailed: boolean,
  kind: WebviewAuthErrorKind,
): RetryButtonState => {
  const pending = isRetrying && !hasFailed && kind !== 'offline';
  return { disabled: pending, pending };
};

/**
 * Whether to signal READY to the native host. Fires once the WebView shows real
 * content — the app page OR the retry screen OR the login form — i.e. whenever we're
 * NOT showing the spinner.
 *
 * Deliberately does NOT require isWebViewReady. That conjunct created a second
 * blank screen in the exact failure mode this PR exists for: when native never
 * answers requestAuthFromNative, isWebViewReady stays false, so recovery exhausts
 * and the retry screen renders — but READY was never posted, leaving the native
 * loading overlay up on top of it. "Not spinning" is the correct and only condition:
 * every terminal render path must signal.
 */
export const shouldSignalWebviewReady = (s: AuthGateState): boolean =>
  s.isWebView && !shouldShowAuthLoading(s);

/**
 * Whether to ask native for a fresh token again. On receipt, WebView.auth
 * reloads the page onto the authed route, so this is the "try again and load"
 * recovery for both stall modes: the handshake never completed, or brands 401'd.
 * Bounded so it can never loop if native never answers.
 *
 * NOT gated on isLoggedIn, and that is load-bearing rather than an oversight. A
 * logged-out WebView user never gets isWebViewReady, so `!isWebViewReady` keeps the
 * spinner up; the ONLY thing that lifts it is recoveryExhausted becoming true, which
 * only happens by burning the attempts here. Gating this on isLoggedIn would leave
 * those users on a permanent spinner — reintroducing the blank screen this PR fixes.
 * The wrong-terminal-screen problem for logged-out users is fixed in
 * shouldShowWebviewAuthError instead, which is where the harm actually was.
 */
export const shouldAttemptWebviewAuthRecovery = (s: AuthGateState, attempts: number): boolean => {
  if (!s.isWebView) return false;
  if (attempts >= MAX_WEBVIEW_AUTH_RECOVERIES) return false;

  // Never reload while offline. Reloading with no connection replaces a spinner with a
  // genuine network-error page — which, in a WebView with no address bar and no reload
  // affordance, is the unrecoverable blank this file exists to remove — and it burns an
  // attempt that would have been useful once connectivity returned.
  //
  // Separate from brandsFetching, not a duplicate of it. brandsFetching (fed from
  // fetchStatus, so it includes 'paused') protects the brandsStalled arm, where the query
  // is enabled and genuinely in flight. It cannot protect the handshake arm at all:
  // `!isWebViewReady` implies logged-out implies the query is disabled implies
  // `fetchStatus: 'idle'`, so brandsFetching is always false there. isOffline is the only
  // input that observes this state.
  //
  // Declining here is what makes offline TERMINAL in shouldShowWebviewAuthError rather than a
  // spinner: no attempt is spent, so recoveryExhausted can never arrive on this arm, so the
  // gate has to release on isOffline itself.
  if (s.isOffline) return false;

  // Neither "stuck" arm counts while a request is in flight. Reloading mid-request
  // would throw away a slow-but-progressing brands fetch and make a slow connection
  // strictly worse than doing nothing.
  const brandsStalled = s.isLoggedIn && !s.brandsLoaded && !s.brandsErrored;
  const stuckOnSpinner = !s.brandsFetching && (!s.isWebViewReady || brandsStalled);

  // A failed refetch that still has cached data is NOT a reason to reload: the app is
  // rendering fine off that data. Only a data-less failure warrants re-auth.
  //
  // And only an AUTH failure. Asking native for a token cannot fix a 500 or a network
  // fault, so recovering on one buys nothing and costs a full document load: the terminal
  // screen has already rendered by then (shouldShowWebviewAuthError treats a non-auth
  // error as terminal without waiting for the budget), so the reload lands on top of the
  // message the user is reading, and the reloaded page hits the same 5xx. Same conjunct
  // in both functions, so the screen we render and the action we take can never disagree.
  const reAuthCanHelp = s.brandsErrored && !s.brandsLoaded && s.brandsErrorIsAuth;

  return reAuthCanHelp || stuckOnSpinner;
};

/**
 * Recovery is settled — safe to reset the attempt counter.
 *
 * Keyed on having usable brands data, NOT on the absence of an error: React Query
 * keeps `data` when a refetch fails, and in that state the app renders fine, so it is
 * settled. Requiring !brandsErrored here would leave the counter un-reset (and, with
 * the old recovery condition, schedule a reload) purely because a background refetch
 * blipped.
 */
export const isWebviewAuthSettled = (s: AuthGateState): boolean =>
  s.isWebView && s.isWebViewReady && s.brandsLoaded;

/**
 * Whether a new session just began, so the RECOVERY budget should be handed back.
 *
 * One budget, not both. The AUTH_TOKEN navigation budget is never handed back at all — see
 * WebViewAuthService.navigations, which explains why no moment inside a freshly loaded document
 * is outside its hazard window, this edge included. An earlier version of this docblock opened
 * by claiming both were reset here while closing by admitting only one was; a plural name on a
 * function with one caller resetting one counter is an invitation to "finish the job" and wire
 * the navigation budget in, which is precisely the self-refilling bound the rest of this file
 * argues against.
 *
 * The recovery budget needs handing back because isWebviewAuthSettled requires brandsLoaded, so
 * it cannot cover attempts spent BEFORE any session existed: a logged-out WebView user burns
 * them while filling in the login form, then logs in with the counter already at MAX, and the
 * first brands failure afterwards tells someone who authenticated seconds ago that their
 * session expired with nothing left to recover with.
 *
 * Keyed on a session epoch that `startSession()` bumps only when it began from a LOGGED-OUT
 * state. Every simpler edge is reachable from the loop itself, which is the whole difficulty —
 * a loop made of document loads cannot be bounded by anything that resets once per document:
 *  - the isLoggedIn false→true edge is what adopting a native token DOES
 *    (Auth.store.handleWebViewToken), so the reply the budget bounds would clear its own bound.
 *  - the logout edge fires on `refresh()` → `endSession()`, i.e. once per document on exactly
 *    the failure path: reply navigates → refresh fails → budget cleared → reply navigates.
 *  - an epoch bumped on ANY `startSession()` fires on the boot `refresh()` SUCCESS path, also
 *    once per document, and additionally in the sibling WebViews, because native writes CF's
 *    JWT key directly (mobile-app useWebviewAuth.ts:219) and that raises a cross-document
 *    `storage` event which useAuth's handler turns into a startSession.
 *
 * Requiring a logged-out start excludes all three: a token refreshed under a session we already
 * had is not a new session, and only an interactive login (or a first login in another document)
 * clears the budget. An AUTH_TOKEN reply cannot reach startSession at all — handleWebViewToken
 * writes the JWT and the flag directly.
 */
export const shouldResetRecoveryBudget = (sessionEpoch: number, seenEpoch: number): boolean =>
  sessionEpoch > seenEpoch;
